/* assets/vc-worn-album.js — <vc-worn-album>: the Worn album, "The Victorian Album" (spec D10, D11; task-17-brief.md). The markup, the
 * two JSON blocks and the CSS are sections/vc-worn-album.liquid; the card rules (the frame, the crop geometry, the caption, the name), the
 * D11 height bands and the filter are assets/vc-logic.js (wornFrame, wornGeometry, wornMeasure, wornWho, wornLabel, HEIGHT_BANDS, RULER,
 * bandOf, bandCounts, filterPortraits, clampHeight, hydratePortraits), shared with the Worn strip.
 *
 * Ported from prototypes/worn-a.html v4 (its script :842-1857), function by function: the masonry set in pages of 12 with a folio between
 * them and its FLIP-by-transform, the Index view, the source index with its sliding rule, the colourway swatches, the Piece listbox, the
 * tailor's rule with its histogram and "your height" needle, the per-wearer chip, the summary strip and the mobile Refine sheet, the
 * lightbox (true proportion, fit notes, shop the look, the near-height row), the live region and the chunked mounting. Changed (brief):
 *   - The photographs and wearers are the section's JSON through hydratePortraits() (`fetch(manifest)` is gone), the colourways its second
 *     JSON block (the swatches, the order, the mood, the series). `HZ`, `RMIN`, `RMAX` are HEIGHT_BANDS and RULER; the filter is
 *     filterPortraits(); the caption, name and accessible name are wornMeasure(), wornWho() and wornLabel(). `SRC` keeps only its extra words
 *     (WORN_SOURCE has the tag); `CW`, `PRICE`, `NOUN`, `MOUNT` and `cutOf` are gone: a price is the worn product's own (formatMoney with
 *     shop.money_format), a product's link, name and picture are its own, the "same jacket" noun is the piece's last word, and the mount
 *     is by the print's ratio (a square one a cushion, the rest arches).
 *   - A wearer's role is not in the JSON: the portrait's source stands for it. A portrait with no colourway is "Curated".
 *     A sweet colourway turns the lightbox panel rose; Porcelain Blue, the one blue chapter (DESIGN.md §3), turns it blue.
 *   - The prototype's `?state=` review aids (filtered, hover, lightbox…) and `?dup=` are gone. `?state=empty` forces the empty state,
 *     `?cw=<handle>` (the strip's "See all on Worn") picks a colourway and `#<portrait id>` (its cards) opens that portrait.
 *   - Motion sits in DESIGN.md §4.4's 600–1400ms band (the prototype's .25–.5s swaps are 600ms), read live under prefers-reduced-motion.
 *   - The site header is sticky, so the desktop strip and the phone's Refine bar sit under it (--vc-header-h) and the scroll margins add it.
 *   - The window and document listeners and the observers are taken up on connect and let go on disconnect (the theme editor re-renders).
 * '@vc/logic' is the import map's name (snippets/scripts.liquid) for the VERSIONED vc-logic.js; a relative import would drop the ?v=.
 */
import {
  HEIGHT_BANDS, RULER, WORN_SOURCE, bandCounts, bandOf, clampHeight, escapeHtml as esc, filterPortraits, formatMoney, hydratePortraits,
  roman, shortTitle, sortByNumeral, wornFrame, wornGeometry, wornLabel, wornMeasure, wornWho,
} from '@vc/logic';

const CHUNK = 60, PAGE = 12, NEAR_R = 5, YOU_R = 5, IX_ROWS = 6, IX_AUTO = 60;   // worn-a.html:884 (YOU_R = NEAR_R: one idea of "near")
const EDGES = [RULER.min, 160, 165, 170, RULER.max];   // the four D11 bands' edges on the rule, in HEIGHT_BANDS order (vc-logic bandOf: <160 lo, <=165 m1, <=170 m2)
const TILT = [-0.55, 0.35, -0.2, 0.5, -0.4, 0.15, 0.6, -0.3, 0.25, -0.5, 0.4, -0.15];   // worn-a.html:880
const NUMW = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const SRC = { atelier: { plate: 'Atelier fitting', plural: 'atelier', sum: 'Atelier', say: 'from the atelier' },   // worn-a.html:855, and the announcement
  muse: { plate: 'Muse', plural: 'muses', sum: 'Muses', say: 'worn by our muses' }, client: { plate: 'Client portrait', plural: 'clients', sum: 'Clients', say: 'sent in by clients' } };
const SRCS = [['all', 'All'], ['atelier', 'Atelier'], ['muse', 'Muses'], ['client', 'Clients']];   // worn-a.html:1231
const BAND_SAY = { lo: 'under 160 cm', m1: 'between 160 and 165 cm', m2: 'between 165 and 170 cm', hi: 'over 170 cm' };   // worn-a.html:1386
const CURATED = 'curated';                // the filter value for a portrait with no colourway (the partner ateliers' pieces)
const CURATED_NAME = 'From partner ateliers';   // the store's own word for the Curated line (templates/collection.curated.json), not "other houses"
const TONE = { 'porcelain-blue': 'blue' };   // DESIGN.md §3: "Porcelain-blue may tint toward #eef2f6 / #7d93ad"
const LS_KEY = 'vc-worn.height';
const SWAP_MS = 620;                       // a swap runs 600ms of CSS (§4.4) and a little more
const BOTTOM_KEEP = 1400;

const num = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));
const fin = (v) => { const n = num(v); return Number.isFinite(n) ? n : null; };
const pad2 = (n) => (n < 10 ? '0' : '') + n;
const list = (a) => (a.length < 2 ? a.join('') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]);
const oxford = (a) => (a.length < 2 ? a.join('') : a.slice(0, -1).join(', ') + ' &amp; ' + a[a.length - 1]);   // the lightbox h2's "A, B & C" (markup)
const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);
function lsGet() { try { const v = parseInt(localStorage.getItem(LS_KEY), 10); return v >= RULER.min && v <= RULER.max ? v : null; } catch (e) { return null; } }
function lsSet(v) { try { localStorage.setItem(LS_KEY, String(v)); } catch (e) { /* storage blocked: the marker still works for this visit */ } }
function lum(hex) {   // relative luminance of #rgb / #rrggbb (WCAG 2.2), or NaN
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return NaN;
  const h = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
  const c = [0, 2, 4].map((i) => { const v = parseInt(h.slice(i, i + 2), 16) / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
const swatch = (c, cls) => '<i class="vc-swatch' + (cls ? ' ' + cls : '') + '" aria-hidden="true" style="--a:' + esc(c.a) + ';--b:' + esc(c.b) + '"></i>';   // snippets/vc-swatch.liquid's markup, built here

function mount(root) {
  const $ = (s, el) => (el || root).querySelector(s);
  const $$ = (s, el) => [...(el || root).querySelectorAll(s)];
  const html = document.documentElement;
  const mqMobile = window.matchMedia('(max-width:720px)');
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = () => mqReduce.matches;
  const params = new URLSearchParams(location.search);
  const money = (cents) => formatMoney(cents, root.dataset.moneyFormat);
  const headerH = () => parseFloat(getComputedStyle(html).getPropertyValue('--vc-header-h')) || (mqMobile.matches ? 56 : 72);

  /* ---------- data: the section's two JSON blocks ---------- */
  const read = (sel) => { try { return JSON.parse($(sel).textContent); } catch (err) { console.warn('[vc-worn-album]', sel, err); return []; } };
  const cws = sortByNumeral(read('script[data-vc-worn-colourways]'), 'numeral');   // the swatches, I to VI
  const CW = {}; cws.forEach((c) => { CW[c.handle] = c; });
  const photos = hydratePortraits(read('script[data-vc-worn]')).filter((p) => p && p.thumb && num(p.w) > 0 && num(p.h) > 0).map(wornFrame).map(prep);
  if (!photos.length) { root.classList.add('is-empty'); return null; }
  const PIECE = new Map();   // piece key -> the label of the first product that carries it (its title without the colourway: "Tiered Skirt")
  const WHO = {};            // wearer id -> { name, h, n (portraits) }

  function prep(p, i) {   // worn-a.html:1801-1819: the wearers of the frame, who they are, how the card stands
    const cw = CW[p.colourway] || null;
    const ppl = (p.wearers || []).map((q) => ({ ...q, name: q.name || q.handle || WORN_SOURCE[p.source] || 'Guest', size: q.size || '', role: p.source,
      h: fin(q.height_cm), bust: fin(q.bust_cm), waist: fin(q.waist_cm), fit: q.fit || null }));
    if (!ppl.length) ppl.push({ id: p.id + '|0', name: WORN_SOURCE[p.source] || 'Guest', size: '', role: p.source, h: null, bust: null, waist: null, fit: null });
    const tone = cw && cw.mood === 'sweet' ? (TONE[cw.handle] || 'sweet') : '';   // the panel of the lightbox, and the card's details
    // A worn product Liquid could not resolve (a draft, archived or unpublished product) arrives as {"title": null, "url": null, …} with a null piece
    // (snippets/vc-worn-json.liquid): one keep mask filters BOTH lists, so products[j] and pieces[j] stay the same product (batch review 2). Left in, it
    // was a blank "£0.00" shop-the-look row, an empty fit-link href and a stray comma in the alt text.
    const all = p.products || [], keep = all.map((x) => !!(x && x.url && x.title));
    const products = all.filter((_, j) => keep[j]), pieces = (p.pieces || []).filter((_, j) => keep[j]);
    return { ...p, i, no: pad2(i + 1), wearers: ppl, ppl, wids: ppl.map((q) => q.id), name: ppl.map((q) => q.name).join(' & '), cw, tone, stock: tone || 'noir',
      series: cw ? cw.series || null : null, shape: p.ar === 1 ? 'cushion' : 'arch', tilt: TILT[i % TILT.length], products, pieces };
  }
  photos.forEach((p) => {
    p.pieces.forEach((k, j) => { if (k && !PIECE.has(k)) PIECE.set(k, (p.products[j] && shortTitle(p.products[j].title, p.colourwayName)) || k); });
    p.ppl.forEach((q) => { const w = WHO[q.id] || (WHO[q.id] = { name: q.name, h: q.h, n: 0 }); w.n++; });
  });
  const PIECES = [...PIECE.entries()].sort((a, b) => a[1].localeCompare(b[1]));

  const st = { src: 'all', cw: 'all', pc: 'all', h: 'all', you: 160, who: 'all', view: 'album' };
  let cards = [], visible = [], visIdx = new Map(), shown = new Set(), limit = CHUNK;
  let lbIdx = -1, lbOpen = false, laidOut = false, lifted = null, folioEls = [], cols = 4, lastW = 0, capH = 64, busy = false, io = null;
  const album = $('.js-album'), folioWrap = $('.js-folios'), page = $('.js-page'), filters = $('.js-filters'), strip = $('.js-strip');
  const stage = $('.js-stage'), lb = $('.js-lb'), lbcard = $('.js-lbcard'), info = $('.js-info'), panel = $('.js-panel'), scroller = $('.scroller', lb);

  /* ---------- the filter (vc-logic filterPortraits), with Curated and a skipped filter for the counts ---------- */
  function pool(skip) {   // the portraits that pass every filter but `skip` (worn-a.html:908 matches(p, s, skip))
    const curated = skip !== 'cw' && st.cw === CURATED;
    return filterPortraits(curated ? photos.filter((p) => !p.colourway) : photos, {
      source: skip === 'src' ? 'all' : st.src, colourway: skip === 'cw' || curated ? 'all' : st.cw, piece: skip === 'pc' ? 'all' : st.pc,
      wearer: skip === 'who' ? 'all' : st.who, band: skip === 'h' ? 'all' : st.h, you: st.you, radius: YOU_R });
  }
  const isFiltered = () => st.src !== 'all' || st.cw !== 'all' || st.pc !== 'all' || st.h !== 'all' || st.who !== 'all';
  const inBand = (h) => h !== null && (st.h === 'you' ? Math.abs(h - st.you) <= YOU_R : bandOf(h) === st.h);

  /* ---------- card markup ---------- */
  const followed = (p) => (st.who !== 'all' && p.wids.indexOf(st.who) >= 0 ? p.ppl[p.wids.indexOf(st.who)] : null);
  const line1 = (p) => wornWho(p, p.i, 'vc-worn');   // the atelier its plate number, a muse or client her own name
  const byH = (p) => p.ppl.slice().sort((a, b) => (a.h === null) - (b.h === null) || a.h - b.h);
  const sameSize = (p) => p.ppl.every((q) => q.size === p.ppl[0].size);
  const PD = '<i class="pd">·</i>';
  function pairsHTML(p) {   // worn-a.html:954: up to three pairs, heights ascending, "151 XS · 158 S"; a fourth wearer is counted, never dropped silently
    const a = byH(p), few = a.slice(0, 3), more = a.length - few.length;
    if (sameSize(p) && p.ppl[0].size) return '<span class="pr">' + few.map((q) => '<span class="n">' + (q.h === null ? '—' : q.h) + '</span>').join(PD) + ' cm</span> <span class="pr"><span class="wr">' + (a.length === 2 ? 'both' : 'all') + '</span> <span class="n">' + esc(a[0].size) + '</span></span>';
    return few.map((q) => '<span class="pr"><span class="n">' + (q.h === null ? '—' : q.h) + '</span>' + (q.size ? '<span class="z">' + esc(q.size) + '</span>' : '') + '</span>').join(PD) + (more ? ' <span class="plus">+' + more + '</span>' : '');
  }
  function single(q, plus) {   // "165 cm · wears S"
    const parts = [];
    if (q.h !== null) parts.push('<span class="n">' + q.h + '</span> cm');
    if (q.size) parts.push('<span class="wr">wears </span><span class="n">' + esc(q.size) + '</span>');
    return '<span>' + parts.join(' · ') + plus + '</span>';
  }
  // caption: l0 the measurement first (phone + index); l1 who + source; l2 the measurements; l3 the colourway on its own line (v4 fix 9). Following one
  // wearer (st.who), every caption leads with HER: "Perpetua" / "160 cm · wears S  +2"
  function capHTML(p) {
    const c = p.cw, sw8 = c ? swatch(c, 'sw8') : '';
    const f = followed(p), grp = p.ppl.length > 1;
    let l0, l1, l2;
    if (f || !grp) {
      const q = f || p.ppl[0], plus = f && grp ? ' <span class="plus">+' + (p.ppl.length - 1) + '</span>' : '';
      l0 = wornMeasure([q], 'vc-worn') + plus;
      l1 = f ? (p.source === 'atelier' ? '<span class="vc-worn__no">No. ' + p.no + '</span> · ' : '') + esc(f.name) : line1(p);
      l2 = single(q, plus);
    } else { l0 = wornMeasure(p.ppl, 'vc-worn'); l1 = line1(p); l2 = '<span class="prs">' + pairsHTML(p) + '</span>'; }
    return '<span class="l0"><span class="ms">' + l0 + '</span>' + sw8 + '<i class="dm"></i></span>' +
      '<span class="l1"><span class="who">' + l1 + '</span><span class="tag">' + WORN_SOURCE[p.source] + '</span></span>' +
      '<span class="l2">' + l2 + '</span>' +
      '<span class="l3">' + (c ? sw8 + '<span class="cw">' + esc(c.name) + '</span>' : '<span class="cw solo">Curated, partner ateliers</span>') + '</span>';
  }
  function drawCap(el, p) { const k = st.who + '|' + (followed(p) ? 1 : 0); if (el._ck === k) return; el._ck = k; $('.wa-cap', el).innerHTML = capHTML(p); el._fit = 0; }
  const collective = (p) => (NUMW[p.ppl.length] ? NUMW[p.ppl.length][0].toUpperCase() + NUMW[p.ppl.length].slice(1) : p.ppl.length) + ' ' + ({ muse: 'muses', client: 'clients', atelier: 'fittings' }[p.source] || 'wearers');
  function fitNames(els) {   // a group name line that would overflow becomes the collective ("Three muses")
    els.forEach((el) => {
      const p = photos[+el.dataset.i]; if (p.ppl.length < 2 || p.source === 'atelier' || followed(p) || el._fit === el._x + '|' + lastW) return;
      const w = $('.who', el); if (!w.clientWidth) return;
      w.innerHTML = line1(p); if (w.scrollWidth > w.clientWidth + 1) w.textContent = collective(p);
      el._fit = el._x + '|' + lastW;
    });
  }
  // the studio imprint by source: the house never signs a client's photograph
  const impHTML = (p) => (p.source === 'client' ? '<span class="wa-imp"><span>Lent by</span><i class="d"></i><span class="h">' + esc(p.name) + '</span></span>'
    : '<span class="wa-imp"><span>Vipercat</span><i class="d"></i><span>' + WORN_SOURCE[p.source] + '</span></span>');
  const stockHTML = (p, big) => '<span class="wa-stock"><span class="wa-win"><span class="wa-open"><img alt="" decoding="async"' + (big ? ' src="' + esc(p.image || p.thumb) + '"' : '') + '><i class="wa-tone"></i></span></span>' + impHTML(p) + '</span>';
  const ariaOf = (p) => wornLabel(p, p.i).replace(/See it in the Worn album\.$/, 'Open the portrait.');
  function buildCard(p) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'wa-card no-anim is-out st-' + p.stock + ' src-' + p.source + (p.ppl.length > 1 ? ' is-grp' : '');
    b.dataset.i = p.i;
    b.style.setProperty('--tilt', p.tilt + 'deg');
    b.setAttribute('aria-label', ariaOf(p));
    b.innerHTML = stockHTML(p) + '<span class="wa-cap" aria-hidden="true"></span>';
    drawCap(b, p);
    b.addEventListener('click', () => openLB(p.i, b, false));
    b.addEventListener('transitionend', (e) => { if (e.target === b && e.propertyName === 'transform') b.classList.remove('is-moving'); });
    cards[p.i] = b;
    if (io) io.observe(b); else load(b);
    return b;
  }

  /* ---------- geometry: a cabinet card is margin + mat opening + a deeper foot for the studio imprint (worn-a.html:1005-1022) ---------- */
  function geom(cw, ar, shape, big, small) {
    const p = Math.round(cw * 0.075), ow = cw - 2 * p, oh = Math.round(ow / ar);
    const ib = small ? 12 : Math.max(big ? 44 : 26, Math.round(cw * 0.125));
    const o = big ? 9 : 6;
    let r, r2;
    if (shape === 'arch') {
      const v = Math.round(ow * 0.42), hh = ow / 2;
      r = hh + 'px ' + hh + 'px 3px 3px / ' + v + 'px ' + v + 'px 3px 3px';
      r2 = (hh + o) + 'px ' + (hh + o) + 'px ' + (3 + o) + 'px ' + (3 + o) + 'px / ' + (v + o) + 'px ' + (v + o) + 'px ' + (3 + o) + 'px ' + (3 + o) + 'px';
    } else if (shape === 'oval') { r = '50%'; r2 = '50%'; } else { const c = Math.round(ow * (shape === 'plate' ? 0.022 : 0.09)); r = c + 'px'; r2 = (c + o) + 'px'; }   // plate: the lightbox's opening
    return { cw, p, ow, oh, ib, sh: p + oh + ib, rad: r, rad2: r2, key: cw + '|' + oh + '|' + ib + '|' + shape };
  }
  function applyGeom(el, g) {
    if (el._gk === g.key) return; el._gk = g.key;
    [['--cw', g.cw + 'px'], ['--p', g.p + 'px'], ['--ow', g.ow + 'px'], ['--oh', g.oh + 'px'], ['--ib', g.ib + 'px'], ['--sh', g.sh + 'px'], ['--rad', g.rad], ['--rad2', g.rad2]]
      .forEach(([k, v]) => el.style.setProperty(k, v));
  }
  function frameImg(img, p, A) {   // the head-to-shoe span fits the opening, never distorted: vc-logic wornGeometry (worn-a.html:1029-1049 geo)
    const g = wornGeometry(p, A), s = img.style;
    s.left = g.l.toFixed(3) + '%'; s.top = g.tp.toFixed(3) + '%'; s.width = g.w.toFixed(3) + '%'; s.height = g.h.toFixed(3) + '%';
  }
  function fpOf(p) {   // object-position for small cover crops (the near row): the subject's, or the group's, centre; head top
    const c = p.cut, x = c.left != null ? (c.left + c.right) / 2 : (c.x == null ? 50 : c.x);
    return Math.round(x) + '% ' + (c.top == null ? 0 : Math.max(0, Math.round(c.top - 8))) + '%';
  }

  /* ---------- masonry, set in pages of 12 with a folio between them (pure: no DOM writes; worn-a.html:1054-1109) ---------- */
  function capOf(p) {   // a phone (or index) caption of three pairs wraps to a second line (v4 fix 4)
    const two = p.ppl.length >= 3 && !followed(p) && (st.view === 'index' ? cols <= 4 : cols === 2);
    return capH + (two ? (st.view === 'index' ? 16 : 18) : 0);
  }
  function computeLayout(items) {
    if (st.view === 'index') return computeIndex(items);
    const W = album.clientWidth; lastW = W;
    cols = W >= 1000 ? 4 : W >= 700 ? 3 : 2;
    const small = cols === 2;
    const gap = cols === 4 ? 44 : cols === 3 ? 36 : 14, rowGap = cols >= 3 ? 48 : 28;
    const cw = Math.floor((W - gap * (cols - 1)) / cols);
    const stagger = cols >= 3 ? 64 : 32, fAbove = cols >= 3 ? 40 : 26, fBelow = cols >= 3 ? 56 : 40;
    capH = cols >= 3 ? 84 : 50;   // l1 + l2 + l3 (desktop) / l0 + l1 (phone)
    const hts = []; for (let c = 0; c < cols; c++) hts.push(c % 2 ? stagger : 0);
    const out = [], folios = [];
    items.forEach((p, k) => {
      if (k && k % PAGE === 0) {   // turn the page: square the columns off, draw the folio, start the next spread
        const bottom = Math.max(...hts) - rowGap;
        folios.push({ y: bottom + fAbove, n: k / PAGE + 1 });
        for (let c = 0; c < cols; c++) hts[c] = bottom + fAbove + fBelow + (c % 2 ? stagger : 0);
      }
      let best = 0; const kp = k % PAGE;
      if (kp < cols) best = kp;   // each spread opens with one portrait per column, left to right
      else for (let c = 1; c < cols; c++) if (hts[c] < hts[best] - 1) best = c;
      const g = geom(cw, p.ar, p.shape, false, small), ch = capOf(p);
      out.push({ p, x: Math.round(best * (cw + gap)), y: Math.round(hts[best]), g, A: p.ar, ch });
      hts[best] += g.sh + ch + rowGap;
    });
    return { items: out, folios, H: items.length ? Math.max(...hts) - rowGap : 0 };
  }
  function computeIndex(items) {   // a contact sheet of the same cards, uniform 1:1.3 openings, no imprint, six across at 1100+ (three on a phone)
    const W = album.clientWidth; lastW = W;
    const n = W >= 1100 ? 6 : W >= 1000 ? 5 : W >= 700 ? 4 : 3; cols = n;
    const gap = n === 3 ? 10 : n === 6 ? 16 : 24, rowGap = n === 3 ? 16 : 26;
    const cw = Math.floor((W - gap * (n - 1)) / n);
    const fAbove = n === 3 ? 22 : 34, fBelow = n === 3 ? 34 : 50;
    capH = n === 3 ? 21 : 26;
    const out = [], folios = []; let y = 0;
    for (let r0 = 0; r0 < items.length; r0 += n) {
      const row = items.slice(r0, r0 + n), k = r0 / n;
      if (k && k % IX_ROWS === 0) { folios.push({ y: y - rowGap + fAbove, n: k / IX_ROWS + 1 }); y += fAbove + fBelow - rowGap; }
      let tall = 0;
      row.forEach((p, j) => {
        const g = geom(cw, 1 / 1.3, p.shape === 'cushion' ? 'arch' : p.shape, false, true), ch = capOf(p);
        out.push({ p, x: Math.round(j * (cw + gap)), y, g, A: 1 / 1.3, ch });
        tall = Math.max(tall, g.sh + ch);
      });
      y += tall + rowGap;
    }
    return { items: out, folios, H: items.length ? y - rowGap : 0 };
  }

  /* ---------- apply the filter state: FLIP-by-transform, animating only what is near the viewport (worn-a.html:1111-1203) ---------- */
  const afterPaint = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));
  function toStatus() {   // the status line (count, wearer chip, view) clears the site header and the strip
    const top = $('.wa-status').getBoundingClientRect().top + window.scrollY - (headerH() + (mqMobile.matches ? 48 : 56) + 8);
    if (window.scrollY > top) window.scrollTo({ top, behavior: 'instant' });   // Atelier's html { scroll-behavior: smooth } would animate it
  }
  function apply(animate) {
    visible = pool();
    visIdx = new Map(); visible.forEach((p, k) => visIdx.set(p.i, k));
    const user = animate;
    animate = animate && !reduced() && laidOut;
    if (user && laidOut) { limit = CHUNK; toStatus(); }   // a new selection starts at the top of the album, mounted afresh in one chunk
    const items = visible.slice(0, limit);
    const L = computeLayout(items);
    const ar = album.getBoundingClientRect(), vh = window.innerHeight;
    const lo = -ar.top - 1.5 * vh, hi = -ar.top + 2.5 * vh;   // album coordinates of scrollY ± 1.5 × innerHeight
    const nearView = (y, h) => y + (h || 0) > lo && y < hi;
    const next = new Set(); items.forEach((p) => next.add(p.i));

    shown.forEach((i) => {   // leaving
      if (next.has(i)) return;
      const el = cards[i]; clearTimeout(el._t); el.classList.remove('is-moving');
      if (animate && nearView(el._y, el._h)) {
        el.classList.remove('no-anim'); el.classList.add('is-out');
        el._t = setTimeout(() => { if (el.classList.contains('is-out')) el.classList.add('is-gone'); }, SWAP_MS);
      } else el.classList.add('no-anim', 'is-out', 'is-gone');
    });

    // DOM order = visual reading order (y, then x), so Tab walks the album as the eye does
    const sorted = L.items.slice().sort((a, b) => a.y - b.y || a.x - b.x);
    const focused = document.activeElement; let cursor = folioWrap.nextSibling, moved = 0;
    sorted.forEach((it) => {
      const el = cards[it.p.i] || buildCard(it.p);
      if (el === cursor) cursor = el.nextSibling; else { album.insertBefore(el, cursor); moved++; }
    });
    if (moved && focused && focused !== document.activeElement && album.contains(focused)) focused.focus({ preventScroll: true });
    if (moved && animate) void album.offsetHeight;   // commit the old positions of re-inserted nodes, so their move can transition

    const fadeIn = [], still = [];
    L.items.forEach((it) => {
      const el = cards[it.p.i], was = shown.has(it.p.i), same = el._x === it.x && el._y === it.y;
      applyGeom(el, it.g);
      if (el._A !== it.A) { el._A = it.A; frameImg($('img', el), it.p, it.A); }
      const isNear = animate && nearView(it.y, it.g.sh + it.ch);
      drawCap(el, it.p);
      if (!was) {
        clearTimeout(el._t); el.classList.remove('is-gone', 'is-moving'); el.classList.add('no-anim');
        if (isNear) { el.classList.add('is-out'); fadeIn.push(el); } else { el.classList.remove('is-out'); still.push(el); }
      } else if (same) return;
      else if (animate && (nearView(el._y, el._h) || isNear)) { el.classList.remove('no-anim'); el.classList.add('is-moving'); }
      else { el.classList.add('no-anim'); still.push(el); }
      el.style.setProperty('--x', it.x + 'px'); el.style.setProperty('--y', it.y + 'px');
      el._x = it.x; el._y = it.y; el._h = it.g.sh + it.ch;
    });
    album.classList.toggle('no-anim', !animate);
    album.style.height = L.H + 'px';

    L.folios.forEach((f, k) => {   // folios
      let el = folioEls[k];
      if (!el) { el = document.createElement('div'); el.className = 'wa-folio no-anim is-off'; el.innerHTML = '<i></i><span></span><i></i>'; folioWrap.appendChild(el); folioEls.push(el); }
      if (el.classList.contains('is-off') || !animate) { el.classList.add('no-anim'); el.classList.remove('is-off'); }
      el.style.setProperty('--y', f.y + 'px');
      $('span', el).textContent = '— ' + roman(f.n).toLowerCase() + ' —';
    });
    for (let k = L.folios.length; k < folioEls.length; k++) folioEls[k].classList.add('is-off');

    if (fadeIn.length) {   // only the few portraits that arrive on screen get a staggered fade
      void album.offsetHeight;
      fadeIn.forEach((el, n) => {
        el.classList.remove('no-anim');
        el.style.transitionDelay = Math.min(n * 45, 540) + 180 + 'ms';
        el.classList.remove('is-out');
        el._t = setTimeout(() => { el.style.transitionDelay = ''; }, 1400);
      });
    }
    fitNames(L.items.filter((it) => it.p.ppl.length > 1).map((it) => cards[it.p.i]));
    afterPaint(() => {   // re-arm transitions once the snapped positions have been painted
      still.forEach((el) => { if (!el.classList.contains('is-out')) el.classList.remove('no-anim'); });
      folioEls.forEach((el) => el.classList.remove('no-anim'));
      album.classList.remove('no-anim');
    });

    shown = next; laidOut = true;
    updateUI();
    if (user) announce();
    requestAnimationFrame(checkMore);
  }

  /* ---------- mount in chunks of 60 as the reader nears the end (worn-a.html:1205-1212) ---------- */
  const more = $('.js-more');
  function checkMore() {
    if (limit >= visible.length) return;
    if (more.getBoundingClientRect().top < window.innerHeight + BOTTOM_KEEP) { limit += CHUNK; apply(false); }
  }

  /* ---------- lazy images: 400w / 1100w for the album slots (worn-a.html:1214-1225) ---------- */
  function load(el) {
    const img = $('img', el); if (img.getAttribute('src')) return;
    const p = photos[+el.dataset.i];
    img.onload = () => img.classList.add('ld');
    img.sizes = st.view === 'index' ? '(min-width:1000px) 220px, 32vw' : '(min-width:1000px) 262px, 46vw';
    img.srcset = p.thumb + ' 400w' + (p.image ? ', ' + p.image + ' 1100w' : '');
    img.src = p.thumb;
  }

  /* ---------- filter controls (worn-a.html:1227-1353) ---------- */
  function makeSrcIndex(wrap) {   // the source: an engraved index with a sliding gilt rule
    const rule = $('.rule', wrap), btns = [];
    SRCS.forEach((s) => {
      const b = document.createElement('button'); b.type = 'button'; b.dataset.v = s[0];
      b.innerHTML = s[1] + '<sup></sup>';
      b.addEventListener('click', () => { st.src = s[0]; apply(true); });
      wrap.appendChild(b); btns.push(b);
    });
    return { wrap, btns, place() {
      const on = btns.filter((b) => b.dataset.v === st.src)[0];
      const r = document.createRange(); r.selectNodeContents(on.firstChild);   // text width only (not the count)
      const tr = r.getBoundingClientRect(), wr = wrap.getBoundingClientRect();
      if (!wr.width) return;
      rule.style.width = tr.width + 'px';
      rule.style.transform = 'translateX(' + (tr.left - wr.left) + 'px)';
    } };
  }
  const srcIdx = [makeSrcIndex($('.js-src')), makeSrcIndex($('.js-src2'))];
  const placeRules = () => srcIdx.forEach((x) => x.place());

  const selects = [];
  const closeAll = (except) => selects.forEach((s) => { if (s !== except && s.classList.contains('open')) s._close(); });
  function makeSelect(host, label, getOpts, key) {   // a listbox (the Piece filter): arrows, Home/End, Enter, Escape
    host.innerHTML = '<button type="button" aria-haspopup="listbox" aria-expanded="false"><span class="v"></span>' +
      '<svg viewBox="0 0 11 7" aria-hidden="true"><path d="M.5 .8 5.5 6l5-5.2"/></svg></button>' +
      '<div class="wa-pop"><ul role="listbox" tabindex="-1" aria-label="' + esc(label) + '"></ul></div>';
    const btn = host.firstChild, ul = $('ul', host); let opts = [], act = -1;
    function render() {
      const o = getOpts(); opts = [];
      ul.innerHTML = '';
      o.forEach((x) => {
        const li = document.createElement('li'); li.setAttribute('role', 'option'); li.id = 'wa-' + key + '-' + opts.length;
        li.className = x.n === 0 ? 'is-zero' : '';
        li.setAttribute('aria-selected', st[key] === x.v ? 'true' : 'false');
        li.innerHTML = '<span>' + x.l + '</span>' + (x.n != null ? '<span class="c">' + x.n + '</span>' : '');
        li.addEventListener('click', () => choose(x.v));
        li.addEventListener('mousemove', () => setAct(opts.indexOf(x)));
        x.el = li; opts.push(x); ul.appendChild(li);
      });
    }
    function setAct(k) {
      act = k; opts.forEach((x, j) => x.el.classList.toggle('is-active', j === k));
      if (opts[k]) { ul.setAttribute('aria-activedescendant', opts[k].el.id); opts[k].el.scrollIntoView({ block: 'nearest' }); }
    }
    function open() {
      closeAll(host); render(); host.classList.add('open'); btn.setAttribute('aria-expanded', 'true');
      let k = 0; opts.forEach((x, j) => { if (x.v === st[key]) k = j; }); setAct(k); ul.focus({ preventScroll: true });
    }
    function close(focus) { host.classList.remove('open'); btn.setAttribute('aria-expanded', 'false'); if (focus) btn.focus({ preventScroll: true }); }
    function choose(v) { st[key] = v; close(true); apply(true); }
    btn.addEventListener('click', () => (host.classList.contains('open') ? close() : open()));
    btn.addEventListener('keydown', (e) => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); open(); } });
    ul.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAct(Math.min(opts.length - 1, act + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setAct(Math.max(0, act - 1)); }
      else if (e.key === 'Home') { e.preventDefault(); setAct(0); }
      else if (e.key === 'End') { e.preventDefault(); setAct(opts.length - 1); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (opts[act]) choose(opts[act].v); }
      else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); }
      else if (e.key === 'Tab') close();
    });
    host._close = close; host._open = open;
    host._label = () => { const o = getOpts(true).filter((x) => x.v === st[key])[0]; const v = $('.v', btn); v.textContent = o ? (o.short || o.l) : ''; v.classList.toggle('is-any', st[key] === 'all'); };
    return host;
  }
  const pcOpts = (noCount) => [{ v: 'all', l: 'Every piece', short: 'All', n: noCount ? null : pool('pc').length },
    ...PIECES.map(([k, label]) => ({ v: k, l: esc(label), short: label, n: noCount ? null : pool('pc').filter((p) => p.pieces.includes(k)).length }))];

  /* colourway: the swatches of the store's colourways (metaobject vc_colourway, flat: D15) and "Curated", one roving tab stop, arrows move */
  const swr = $('.js-swr'), cwn = $('.js-cwn'), swBtns = []; let cwHover = null;
  const cwCount = (v) => pool('cw').filter((p) => (v === CURATED ? !p.colourway : p.colourway === v)).length;
  const cwShort = () => (st.cw === 'all' ? '' : st.cw === CURATED ? 'Curated' : (CW[st.cw] ? CW[st.cw].name : st.cw));
  const roving = (on) => swBtns.forEach((b) => { b.tabIndex = b === on ? 0 : -1; });
  function buildSwatches() {
    cws.map((c) => ({ v: c.handle, n: c.name, c })).concat([{ v: CURATED, n: 'Curated' }]).forEach((o) => {
      if (!o.c) { const bar = document.createElement('i'); bar.className = 'bar'; bar.setAttribute('aria-hidden', 'true'); swr.appendChild(bar); }
      const b = document.createElement('button'); b.type = 'button'; b.dataset.v = o.v; b._n = o.n; b._cnt = 0;
      if (o.c) { b.className = 'en' + (lum(o.c.a) < 0.045 && lum(o.c.b) < 0.045 ? ' dk' : ''); b.innerHTML = swatch(o.c); } else { b.className = 'cur'; b.textContent = 'Curated'; }   // dk: Noir and Midnight vanish on the ground
      b.addEventListener('click', () => { st.cw = st.cw === o.v ? 'all' : o.v; roving(b); apply(true); });
      b.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { cwHover = b; drawCwName(); } });
      b.addEventListener('pointerleave', () => { if (cwHover === b) { cwHover = null; drawCwName(); } });
      b.addEventListener('focus', () => { if (b.matches(':focus-visible')) { cwHover = b; drawCwName(); } });
      b.addEventListener('blur', () => { if (cwHover === b) { cwHover = null; drawCwName(); } });
      b.addEventListener('keydown', (e) => {
        const j = swBtns.indexOf(b); let d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (e.key === 'Home') d = -j; if (e.key === 'End') d = swBtns.length - 1 - j;
        if (d == null) return; e.preventDefault();
        const nb = swBtns[(j + d + swBtns.length) % swBtns.length]; roving(nb); nb.focus();
      });
      swr.appendChild(b); swBtns.push(b);
    });
  }
  function drawCwName() {
    const b = cwHover || swBtns.filter((x) => x.dataset.v === st.cw)[0];
    cwn.classList.toggle('is-any', !b);
    cwn.innerHTML = b ? esc(b._n === 'Curated' ? CURATED_NAME : b._n) + '<b>' + b._cnt + '</b>' : 'Any';
  }
  function drawSwatches() {
    swBtns.forEach((b) => {
      b._cnt = cwCount(b.dataset.v);
      b.setAttribute('aria-pressed', st.cw === b.dataset.v ? 'true' : 'false');
      b.classList.toggle('is-zero', !b._cnt);
      b.setAttribute('aria-label', (b._n === 'Curated' ? 'Curated pieces from partner ateliers' : b._n) + ', ' + plural(b._cnt, 'portrait', 'portraits'));
    });
    if (!swr.contains(document.activeElement)) roving(swBtns.filter((b) => b.dataset.v === st.cw)[0] || swBtns[0]);
    drawCwName();
  }

  /* per-wearer filter (worn-c.html st.who): follow one body across pieces and sizes; a removable chip in the status line */
  const chip = $('.js-chip'), whox = $('.js-whox');
  function drawChip() {
    const w = st.who !== 'all' ? WHO[st.who] : null;
    chip.classList.toggle('on', !!w);
    if (!w) { chip._w = null; return; }
    if (chip._w === st.who) return; chip._w = st.who;
    chip.innerHTML = '<span>More on</span><em>' + esc(w.name) + '</em>' + (w.h !== null ? '<i class="dt" aria-hidden="true"></i><b>' + w.h + '<small>cm</small></b>' : '') +
      '<svg viewBox="0 0 9 9" aria-hidden="true"><path d="M.5 .5l8 8M8.5 .5l-8 8"/></svg>';
    chip.setAttribute('aria-label', 'Showing only ' + w.name + (w.h !== null ? ', ' + w.h + ' centimetres' : '') + '. Remove this filter');
  }
  chip.addEventListener('click', () => { st.who = 'all'; apply(true); const v = $('.js-view button[aria-pressed="true"]'); if (v) v.focus({ preventScroll: true }); });
  whox.addEventListener('click', () => { st.who = 'all'; apply(true); $('.js-refine').focus({ preventScroll: true }); });   // the strip's own way out of "More on …"
  function followWearer(id) { if (!WHO[id]) return; st.who = id; closeLB(true); apply(true); chip.focus({ preventScroll: true }); }

  /* album / index (worn-c.html view toggle): the index is the same cabinet stock, small and uniform */
  const viewEl = $('.js-view'), viewBtns = $$('button', viewEl), viewRule = $('.rule', viewEl);
  function drawView() {
    viewBtns.forEach((b) => b.setAttribute('aria-pressed', b.dataset.v === st.view ? 'true' : 'false'));
    const on = viewBtns.filter((b) => b.dataset.v === st.view)[0], wr = viewEl.getBoundingClientRect(), r = on.getBoundingClientRect();
    if (!wr.width) return;
    viewRule.style.width = r.width + 'px'; viewRule.style.transform = 'translateX(' + (r.left - wr.left) + 'px)';
  }
  function setView(v) {
    if (v === st.view) return;
    const go = () => { st.view = v; album.classList.toggle('is-ix', v === 'index'); apply(false); announce(); toStatus(); };
    if (reduced() || !laidOut) { go(); return; }
    album.classList.add('is-swap');
    setTimeout(() => { go(); afterPaint(() => setTimeout(() => album.classList.remove('is-swap'), 20)); }, SWAP_MS);
  }
  viewBtns.forEach((b) => b.addEventListener('click', () => setView(b.dataset.v)));

  /* one polite announcement per filter change (debounced so a drag on the rule speaks once) */
  let liveT = 0;
  function announce() {
    clearTimeout(liveT);
    liveT = setTimeout(() => {
      const n = visible.length, a = [];
      if (st.who !== 'all') a.push('worn by ' + WHO[st.who].name);
      if (st.src !== 'all') a.push(SRC[st.src].say);
      if (st.cw !== 'all') a.push(st.cw === CURATED ? 'curated pieces' : 'in ' + cwShort());
      if (st.pc !== 'all') a.push('with the ' + (PIECE.get(st.pc) || st.pc));
      if (st.h === 'you') a.push('within ' + NUMW[YOU_R] + ' centimetres of ' + st.you + ' cm');
      else if (st.h !== 'all') a.push(BAND_SAY[st.h]);
      $('.js-live').textContent = (n ? n + ' of ' + photos.length + ' portraits' : 'No portraits') + (a.length ? ', ' + a.join(', ') : '') + (st.view === 'index' ? ', shown as an index' : '') + '.';
    }, 450);
  }

  /* ---------- height: the tailor's rule (worn-a.html:1398-1488) ---------- */
  const ruler = $('.js-ruler'), track = $('.js-track'), knob = $('.js-knob'), you = $('.you', ruler), ybr = $('.ybr', ruler), zones = [], bars = [];
  const pctOf = (h) => (h - RULER.min) / (RULER.max - RULER.min) * 100;
  function buildRuler() {
    const ticks = $('.ticks', ruler), hist = $('.hist', ruler);
    for (let h = RULER.min; h <= RULER.max; h += 5) {
      const b = document.createElement('b'); b.style.left = pctOf(h) + '%'; ticks.appendChild(b);
      const s = document.createElement('span'); s.className = 'num' + (h === RULER.min || h === RULER.max ? ' end' : ''); s.style.left = pctOf(h) + '%'; s.textContent = h; ticks.appendChild(s);
    }
    for (let h = RULER.min; h <= RULER.max; h++) { const i = document.createElement('b'); i.style.left = pctOf(h) + '%'; hist.appendChild(i); bars.push(i); }
    HEIGHT_BANDS.forEach((z, zi) => {
      const b = document.createElement('button'); b.type = 'button'; b.className = 'zone z-' + z.key;
      b.title = 'Portraits with a wearer in this range'; b.setAttribute('aria-description', 'portraits with a wearer in this range');
      const a = EDGES[zi], e = EDGES[zi + 1];
      b.style.left = pctOf(a) + '%'; b.style.width = (pctOf(e) - pctOf(a)) + '%';
      b.innerHTML = '<span class="zl">' + esc(z.label) + '<b></b></span>';   // "Under 160 · 8": portraits in the band, given the other filters
      b.addEventListener('click', () => { st.h = st.h === z.key ? 'all' : z.key; apply(true); });
      // the band span stays true to centimetres, but the labels sit in equal slots so the narrow middle bands never collide
      const slot = (zi + 0.5) * 100 / HEIGHT_BANDS.length, wz = pctOf(e) - pctOf(a);
      b.firstChild.style.left = ((slot - pctOf(a)) / wz * 100) + '%';
      ruler.appendChild(b); zones.push(b); b._z = z;
    });
  }
  function drawYou() {
    const on = st.h === 'you';
    ruler.classList.toggle('is-you', on);
    you.style.setProperty('--yx', ruler.clientWidth * pctOf(st.you) / 100 + 'px');
    you.classList.toggle('flip', st.you > 172);
    $('.yl', you).innerHTML = st.you + '<em>you</em>';
    ybr.style.left = pctOf(Math.max(RULER.min, st.you - YOU_R)) + '%';
    ybr.style.width = (pctOf(Math.min(RULER.max, st.you + YOU_R)) - pctOf(Math.max(RULER.min, st.you - YOU_R))) + '%';
    knob.tabIndex = on ? 0 : -1;
    knob.setAttribute('aria-valuenow', st.you);
    knob.setAttribute('aria-valuetext', st.you + ' centimetres; showing wearers within ' + YOU_R + ' centimetres');
  }
  function drawHist() {   // one dot per WEARER (not per photograph) among portraits matching the other filters; gold inside the chosen height
    const seen = {}, tot = new Array(bars.length).fill(0), on = new Array(bars.length).fill(0);
    pool('h').forEach((p) => {
      p.ppl.forEach((q) => {
        if (seen[q.id] || q.h === null || (st.who !== 'all' && q.id !== st.who)) return; seen[q.id] = 1;
        const k = clampHeight(q.h) - RULER.min; tot[k]++; if (st.h !== 'all' && inBand(q.h)) on[k]++;
      });
    });
    bars.forEach((b, k) => {
      let h = ''; for (let j = 0; j < Math.min(4, tot[k]); j++) h += j < on[k] ? '<i class="on"></i>' : '<i></i>';
      if (b._h !== h) { b._h = h; b.innerHTML = h; }
    });
  }
  function drawZones() {
    const bc = bandCounts(pool('h'));
    zones.forEach((b) => {
      const z = b._z, n = bc[z.key];
      b.setAttribute('aria-pressed', st.h === z.key ? 'true' : 'false');
      $('b', b).textContent = n; b.classList.toggle('is-zero', !n);
      b.setAttribute('aria-label', (z.label + ' cm').replace('–', ' to ').replace('cm', 'centimetres') + ', ' + plural(n, 'portrait', 'portraits'));
    });
  }
  function setYou(h, commit) {
    st.you = clampHeight(h); st.h = 'you'; lsSet(st.you);   // the marker clamps to 150–185
    if (commit) apply(true); else { drawYou(); drawHist(); }
  }
  function hFromX(x) { const r = track.getBoundingClientRect(); return RULER.min + (x - r.left) / r.width * (RULER.max - RULER.min); }
  let dragging = false, kt = 0;
  track.addEventListener('pointerdown', (e) => { dragging = true; ruler.classList.add('is-drag'); track.setPointerCapture(e.pointerId); setYou(hFromX(e.clientX), false); });
  track.addEventListener('pointermove', (e) => { if (dragging) { const h = clampHeight(hFromX(e.clientX)); if (h !== st.you) setYou(h, false); } });
  function endDrag() { if (!dragging) return; dragging = false; ruler.classList.remove('is-drag'); apply(true); knob.focus({ preventScroll: true, focusVisible: false }); }
  track.addEventListener('pointerup', endDrag); track.addEventListener('pointercancel', endDrag);
  const focusHNote = () => { const b = $('.js-hnote button'); if (b) b.focus({ preventScroll: true }); };
  knob.addEventListener('keydown', (e) => {
    let d = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1, PageDown: -5, PageUp: 5 }[e.key];
    if (e.key === 'Home') d = RULER.min - st.you; if (e.key === 'End') d = RULER.max - st.you;
    if (e.key === 'Escape' || e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); e.stopPropagation(); st.h = 'all'; apply(true); focusHNote(); return; }
    if (d == null) return;
    e.preventDefault(); setYou(st.you + d, false);
    clearTimeout(kt); kt = setTimeout(() => apply(true), 380);
  });
  function renderHNote() {
    const note = $('.js-hnote'), want = st.h === 'all' ? 'find' : 'any';
    if (note._m === want) return; note._m = want;
    note.innerHTML = want === 'find' ? '<button class="clr" type="button">find someone your height</button>' : '<button class="clr" type="button">Any height</button>';
    $('button', note).addEventListener('click', () => {
      if (want === 'find') { setYou(lsGet() || st.you, true); knob.focus({ preventScroll: true }); } else { st.h = 'all'; apply(true); focusHNote(); }
    });
  }

  /* ---------- summary strip + mobile sheet (worn-a.html:1490-1564) ---------- */
  const hLabel = () => (st.h === 'all' ? '' : st.h === 'you' ? 'Near ' + st.you + ' cm' : HEIGHT_BANDS.filter((z) => z.key === st.h)[0].label + ' cm');
  function summary(withSrc) {
    const a = [];
    if (st.who !== 'all') a.push(WHO[st.who].name);
    if (withSrc && st.src !== 'all') a.push(SRC[st.src].sum);
    if (st.cw !== 'all') a.push(cwShort());
    if (st.pc !== 'all') a.push(PIECE.get(st.pc) || st.pc);
    if (st.h !== 'all') a.push(hLabel());
    return a.join(' · ');
  }
  function updateUI() {
    const bySrc = { all: 0, atelier: 0, muse: 0, client: 0 };
    pool('src').forEach((p) => { bySrc.all++; bySrc[p.source]++; });
    srcIdx.forEach((x) => x.btns.forEach((b) => { b.setAttribute('aria-pressed', st.src === b.dataset.v ? 'true' : 'false'); $('sup', b).textContent = bySrc[b.dataset.v]; }));
    placeRules();
    selects.forEach((s) => s._label());
    drawSwatches(); drawZones();
    drawYou(); drawHist(); renderHNote(); drawChip(); drawView();
    const n = visible.length, filtered = isFiltered();
    $('.js-status').innerHTML = filtered ? 'Showing <b>' + n + '</b> of <b>' + photos.length + '</b> portraits' : 'All <b>' + photos.length + '</b> portraits in the album';
    $('.js-clear').classList.toggle('on', filtered);
    $('.js-clear2').disabled = !filtered;
    $('.js-empty').classList.toggle('on', n === 0);
    const mob = mqMobile.matches, s = summary(mob), sum = $('.js-sum');
    sum.textContent = s || (mob ? 'The whole album' : 'Every portrait');
    sum.classList.toggle('is-any', !s);
    whox.classList.toggle('on', st.who !== 'all');
    if (st.who !== 'all') whox.setAttribute('aria-label', 'Stop following ' + WHO[st.who].name);
    $('.js-cnt').innerHTML = mob ? '<b>' + n + '</b>' + (n === 1 ? 'portrait' : 'portraits') : 'Showing <b>' + n + '</b>';
    $('.js-sheet-go').innerHTML = 'Show <b>' + n + '</b> ' + (n === 1 ? 'portrait' : 'portraits');
  }
  function clearAll() { st.src = 'all'; st.cw = 'all'; st.pc = 'all'; st.h = 'all'; st.who = 'all'; apply(true); }
  $('.js-clear').addEventListener('click', clearAll);
  $('.js-clear2').addEventListener('click', clearAll);

  let sheetOpen = false;
  const lockScroll = (on) => { html.style.scrollbarGutter = on ? 'stable' : ''; html.style.overflow = on ? 'hidden' : ''; };
  function openSheet() {
    sheetOpen = true; filters.classList.add('is-open'); $('.js-scrim').classList.add('on'); lockScroll(true);
    $('#wa-sheet-t').focus({ preventScroll: true });
    requestAnimationFrame(placeRules); drawYou();
  }
  function closeSheet(show) {
    if (!sheetOpen) return; sheetOpen = false; closeAll();
    filters.classList.remove('is-open'); $('.js-scrim').classList.remove('on'); lockScroll(false);
    if (show) toStatus();
    $('.js-refine').focus({ preventScroll: true });
  }
  $('.js-refine').addEventListener('click', () => {
    if (mqMobile.matches) { openSheet(); return; }
    window.scrollTo({ top: filters.getBoundingClientRect().top + window.scrollY - headerH() - 24, behavior: reduced() ? 'auto' : 'smooth' });
    (swBtns.filter((b) => b.tabIndex === 0)[0] || swBtns[0]).focus({ preventScroll: true });
  });
  $('.js-sheet-x').addEventListener('click', () => closeSheet(false));
  $('.js-sheet-go').addEventListener('click', () => closeSheet(true));
  $('.js-scrim').addEventListener('click', () => closeSheet(false));
  filters.addEventListener('keydown', (e) => { if (e.key === 'Escape' && sheetOpen) { e.preventDefault(); closeSheet(false); } });

  /* ---------- lightbox: the card lifted out of the album (worn-a.html:1566-1778) ---------- */
  let lastFocus = null;
  function lbGeom(p) {   // the lifted card's opening takes the print's TRUE proportion, so hems, shoes and headdresses are never cut
    const r = stage.getBoundingClientRect(), mobile = window.innerWidth <= 720, narrow = window.innerWidth <= 1100;
    const availW = r.width - (mobile ? 32 : narrow ? 150 : 170);
    const availH = narrow ? Math.min(window.innerHeight * 0.74, 720) : window.innerHeight - 88;
    const ar = p.ew / p.eh, k = 0.075 + 0.85 / ar + (mobile ? 0.145 : 0.125);
    return geom(Math.floor(Math.min(availW, (availH - 8) / k, mobile ? 358 : 640)), ar, 'plate', true);
  }
  const wornNames = (p) => list(p.ppl.map((q) => q.name));
  function fillCard(p) {
    lbcard.className = 'lbcard js-lbcard st-' + p.stock;
    lbcard.innerHTML = stockHTML(p, true);
    const img = $('img', lbcard), what = p.products.map((x) => shortTitle(x.title, p.colourwayName));
    img.alt = wornNames(p) + (what.length ? ' wearing ' + list(what) : '') + (p.colourwayName ? ' in ' + p.colourwayName : '');
    const k = p.t / (1 - 2 * p.t) * 100, full = 100 / (1 - 2 * p.t);   // a printed border (crop.trim) falls just outside the opening
    img.style.left = img.style.top = (-k).toFixed(3) + '%'; img.style.width = img.style.height = full.toFixed(3) + '%';
    lbcard._gk = null; applyGeom(lbcard, lbGeom(p));
  }
  // the near row: the same piece on a body within 5 cm first, then the same series, then plain height; one portrait per wearer.
  // "The same piece" is the same VIPERCAT garment (same series, any colourway); a portrait with no colourway (Curated) never counts
  function sharedPiece(a, b) { if (!a.series || b.series !== a.series) return null; for (const k of a.pieces) if (k && b.pieces.includes(k)) return k; return null; }
  const nounOf = (k) => { const s = String(PIECE.get(k) || k).trim().split(/\s+/); return s[s.length - 1].toLowerCase(); };   // "Boned Corset" → corset (worn-a.html:861 NOUN)
  function fitLine(p, fw) {   // group wording (v4 fix 12): "Worn here in S, M & XS"; one followed wearer names her own size
    if (fw || p.ppl.length < 2) { const q = fw || p.ppl[0]; return q.size ? esc(q.name) + ' wears <b>' + esc(q.size) + '</b> — choose yours' : 'Choose your size'; }
    const z = []; p.ppl.forEach((q) => { if (q.size && z.indexOf(q.size) < 0) z.push(q.size); });
    return z.length ? 'Worn here in ' + oxford(z.map((x) => '<b>' + esc(x) + '</b>')) + ' — choose yours' : 'Choose your size';
  }
  const POS = (j, n) => (n === 2 ? ['Left', 'Right'][j] : n === 3 ? ['Left', 'Centre', 'Right'][j] : (j + 1) + ' from left');
  function nearBase(p, fw) {   // the followed wearer, else the wearer nearest the reader's saved height, else the first wearer
    if (fw) return fw.h;
    const y = lsGet(), known = p.ppl.filter((q) => q.h !== null);
    if (y == null || known.length < 2) return p.ppl[0].h;
    return known.reduce((a, q) => (Math.abs(q.h - y) < Math.abs(a.h - y) ? q : a)).h;
  }
  function near(p, h) {   // every person in a group portrait counts; the people in this portrait are left out (worn-c.html near())
    const mine = {}, seen = {}, out = [];
    p.wids.forEach((id) => { mine[id] = 1; });
    if (h === null) return out;
    photos.forEach((q) => {
      if (q === p) return;
      let w = null;
      q.ppl.forEach((x) => { if (!mine[x.id] && x.h !== null && Math.abs(x.h - h) <= NEAR_R && (!w || Math.abs(x.h - h) < Math.abs(w.h - h))) w = x; });
      if (!w) return;
      const sp = sharedPiece(p, q);
      out.push({ q, w, sp, tier: sp ? 0 : (p.series && q.series === p.series ? 1 : 2), d: Math.abs(w.h - h) });
    });
    return out.sort((a, b) => a.tier - b.tier || a.d - b.d || a.q.i - b.q.i).filter((x) => { if (seen[x.w.id]) return false; seen[x.w.id] = 1; return true; }).slice(0, 4);
  }
  const stat = (label, v, unit) => {
    const nd = v === null || v === undefined || v === '';
    return '<div><dt>' + label + '</dt><dd' + (nd ? ' class="nd"' : String(v).length > 5 ? ' class="long"' : '') + '>' + (nd ? '—' : esc(v) + (unit ? '<small>' + unit + '</small>' : '')) + '</dd></div>';
  };
  const MORE_ARROW = '<svg viewBox="0 0 18 8" aria-hidden="true"><path d="M0 4h17M13.5 .5 17 4l-3.5 3.5"/></svg>';
  function moreBtns(ppl, inRow) {   // "More on Edith · 158 cm": follow this body across pieces and sizes (sets the per-wearer chip); inRow: the row above already gives her height
    const mo = ppl.filter((q) => WHO[q.id] && WHO[q.id].n > 1 && st.who !== q.id);
    return mo.length ? '<p class="more">' + mo.map((q) => '<button type="button" data-who="' + esc(q.id) + '" aria-label="More portraits of ' + esc(q.name) + (q.h !== null ? ', ' + q.h + ' centimetres' : '') +
      '">More on <em>' + esc(q.name) + '</em>' + (inRow || q.h === null ? '' : '· <b>' + q.h + ' cm</b>') + MORE_ARROW + '</button>').join('') + '</p>' : '';
  }
  const credit = (q) => esc(q.name) + (q.role === 'client' ? ', in their own words' : q.role === 'atelier' ? ', atelier fitting note' : ', muse');
  const isCut = (url) => /-cut[-.]/.test(String(url).split('?')[0]);   // DESIGN.md §1: a cutout's file name has -cut. or -cut- (snippets/vc-product-card.liquid)
  function fillInfo(p) {
    const arrow = '<svg viewBox="0 0 22 8" aria-hidden="true"><path d="M0 4h21M17.5 .5 21 4l-3.5 3.5"/></svg>';
    const group = p.ppl.length > 1, fw = followed(p), q0 = fw || p.ppl[0], mood = p.cw && p.cw.mood === 'sweet' ? 'sweet' : 'noir';
    panel.className = 'panel js-panel' + (p.tone ? ' pg-' + p.tone : '');   // the colourway's chapter: Rose Blanche and Porcelain Blue turn the panel to their ground
    lb.classList.toggle('pg-sweet', p.tone === 'sweet'); lb.classList.toggle('pg-blue', p.tone === 'blue');
    const shop = p.products.map((x) => {
      const th = !x.image ? '<span class="th none" aria-hidden="true"></span>' : '<span class="th' + (isCut(x.image) ? '' : ' ph') + '"><img alt="" loading="lazy" width="40" height="54" src="' + esc(x.image) + '"></span>';
      return '<li><a class="row" href="' + esc(x.url) + '" data-mood="' + mood + '">' + th + '<span class="pt"><span class="pn">' + esc(shortTitle(x.title, p.colourwayName)) + '</span>' +
        (p.colourwayName ? '<span class="pc">in ' + esc(p.colourwayName) + '</span>' : '') + '</span><span class="pr">' + money(x.price) + '</span>' + arrow + '</a></li>';
    }).join('');
    const fit = p.products.length ? '<p class="fit"><a href="' + esc(p.products[0].url) + '" data-mood="' + mood + '">' + fitLine(p, fw) + '</a></p>' : '';
    let who, moreRow = group ? '' : moreBtns(p.ppl);
    if (group) {   // wearers[] order = left to right in the photograph; a followed wearer is lifted to the top, gilt lozenge
      const rows = p.ppl.map((q, j) => ({ q, pos: POS(j, p.ppl.length) }));
      if (fw) rows.sort((a, b) => (b.q === fw) - (a.q === fw));
      who = '<div class="sec wb"><h3>Worn by' + (fw ? '' : '<span class="ltr">left to right</span>') + '<em>' + (NUMW[p.ppl.length] || p.ppl.length) + ' wearers</em></h3><ul class="worn">' + rows.map((o) => {
        const q = o.q;
        return '<li><div class="r"><span class="nm"><span class="rl">' + WORN_SOURCE[q.role] + (fw ? ' · ' + o.pos : '') + '</span>' + esc(q.name) + (q === fw ? '<i class="lz" aria-hidden="true"></i>' : '') + '</span>' +
          '<dl style="display:contents">' + stat('Height', q.h, 'cm') + stat('Wears', q.size) + stat('Bust', q.bust, 'cm') + stat('Waist', q.waist, 'cm') + '</dl></div>' +
          (q.fit ? '<blockquote class="fitnote">' + esc(q.fit) + '</blockquote>' : '') + moreBtns([q], true) + '</li>';
      }).join('') + '</ul></div>';
    } else {
      who = '<dl class="facts">' + stat('Height', q0.h, 'cm') + stat('Wears', q0.size) + (q0.bust != null ? stat('Bust', q0.bust, 'cm') : '') + (q0.waist != null ? stat('Waist', q0.waist, 'cm') : '') + '</dl>' +
        (q0.fit ? '<blockquote class="fitnote">' + esc(q0.fit) + '<cite>' + credit(q0) + '</cite></blockquote>' : '');   // the fit note, in the wearer's own voice
    }
    const nh = nearBase(p, fw), nr = near(p, nh), anySame = nr.some((x) => x.tier === 0);
    const nb = nr.map((x) => {
      const q = x.q, w = x.w;
      return '<li><button type="button" data-i="' + q.i + '" aria-label="' + esc(w.name) + ', ' + w.h + ' centimetres' + (w.size ? ', wears ' + esc(w.size) : '') + (x.tier === 0 ? ', same ' + esc(nounOf(x.sp)) : '') + '">' +
        '<span class="mini"><img alt="" loading="lazy" src="' + esc(q.thumb) + '" style="object-position:' + fpOf(q) + '"></span>' +
        '<span class="m">' + w.h + ' cm' + (w.size ? '<em>wears ' + esc(w.size) + '</em>' : '') + '</span>' + (x.tier === 0 ? '<span class="same">Same ' + esc(nounOf(x.sp)) + '</span>' : '') + '</button></li>';
    }).join('');
    const k = visIdx.has(p.i) ? visIdx.get(p.i) : -1;
    info.innerHTML =
      '<p class="plate"><span class="num">No. ' + p.no + '</span><i></i><span class="t-' + p.source + '">' + SRC[p.source].plate + '</span></p>' +
      '<h2 id="wa-lb-name" tabindex="-1"' + (group ? ' class="grp"' : '') + '>' + p.ppl.map((q, j) => { const n = p.ppl.length;
        return esc(q.name) + (j < n - 2 ? '<span class="cm">,</span> ' : j === n - 2 ? ' <span class="amp">&amp;</span> ' : ''); }).join('') + '</h2>' +
      '<p class="serie">' + (p.cw ? (p.cw.seriesName ? esc(p.cw.seriesName) + '<i class="d" aria-hidden="true"></i>' : '') + '<span class="cwv">' + swatch(p.cw, 'sw') + esc(p.cw.name) + '</span>' : 'Curated, from partner ateliers') + '</p>' +
      who + moreRow +
      (p.products.length ? '<div class="sec"><h3>Shop the look<em>' + plural(p.products.length, 'piece', 'pieces') + '</em></h3><ul class="shop">' + shop + '</ul>' + fit + '</div>' : '') +
      (nb ? '<div class="sec"><h3>' + (anySame ? 'Near ' + nh + ' cm, in this piece' : 'Wearers near ' + nh + ' cm') + '<em>within ' + NEAR_R + ' cm</em></h3><ul class="near">' + nb + '</ul></div>' : '') +
      '<div class="foot"><button type="button" class="fb js-fprev">Previous</button><span class="pos">' + (k >= 0 ? pad2(k + 1) + ' / ' + pad2(visible.length) : '') + '</span><button type="button" class="fb js-fnext">Next</button><span class="keys">← → to turn · Esc to close</span></div>';
    $('.js-fprev', info).addEventListener('click', () => step(-1));
    $('.js-fnext', info).addEventListener('click', () => step(1));
    $$('.near button', info).forEach((b) => b.addEventListener('click', () => show(+b.dataset.i, 0)));
    $$('.more button', info).forEach((b) => b.addEventListener('click', () => followWearer(b.dataset.who)));
    if (lbOpen && !lb.contains(document.activeElement)) $('#wa-lb-name', info).focus({ preventScroll: true });
  }
  function flipFrom(el, rect, dur) {
    const b = el.getBoundingClientRect();
    el.style.transition = 'none';
    el.style.transform = 'translate(' + (rect.left - b.left) + 'px,' + (rect.top - b.top) + 'px) scale(' + (rect.width / b.width) + ',' + (rect.height / b.height) + ')';
    void el.offsetWidth;
    el.style.transition = 'transform ' + dur + 'ms var(--vc-ease-out)';
    el.style.transform = '';
  }
  function markLifted(i) { if (lifted) lifted.classList.remove('is-lifted'); lifted = cards[i] || null; if (lifted) lifted.classList.add('is-lifted'); }
  function openLB(i, fromEl, instant) {
    const p = photos[i]; lbIdx = i; lastFocus = fromEl || document.activeElement;
    lbOpen = true; page.inert = true; lockScroll(true);
    lb.classList.add('on'); lb.setAttribute('aria-hidden', 'false'); scroller.scrollTop = 0;
    fillCard(p); fillInfo(p);
    if (fromEl && !reduced() && !instant) flipFrom(lbcard, $('.wa-stock', fromEl).getBoundingClientRect(), 1150);
    markLifted(i);
    $('#wa-lb-name', info).focus({ preventScroll: true });
  }
  function closeLB(instant) {
    if (!lbOpen) return; lbOpen = false;
    page.inert = false; lockScroll(false);
    const k = visIdx.has(lbIdx) ? visIdx.get(lbIdx) : -1;
    if (k >= limit) { limit = Math.ceil((k + 1) / CHUNK) * CHUNK; apply(false); }
    const el = k >= 0 ? cards[lbIdx] : null; let r;
    if (el && instant !== true) { r = el.getBoundingClientRect(); if (r.bottom < 0 || r.top > window.innerHeight) window.scrollTo({ top: window.scrollY + r.top - Math.max(24, (window.innerHeight - r.height) / 2), behavior: 'instant' }); }
    const onScreen = instant !== true && el && (r = el.getBoundingClientRect()) && r.bottom > 0 && r.top < window.innerHeight;
    const veil = $('.veil', lb);
    function finish() {
      lb.classList.remove('on', 'pg-sweet', 'pg-blue'); lb.setAttribute('aria-hidden', 'true');
      veil.style.transition = ''; veil.style.opacity = '';
      panel.style.transition = 'none'; panel.style.opacity = '';
      afterPaint(() => { panel.style.transition = ''; });
      lbcard.style.transition = ''; lbcard.style.transform = '';
      markLifted(-1);
      if (instant === true) return;   // the caller moves focus (following a wearer)
      const to = el || (lastFocus && lastFocus.isConnected && !lastFocus.classList.contains('is-out') ? lastFocus : null);
      if (to) to.focus({ preventScroll: true });
    }
    if (onScreen && !reduced()) {
      const s = $('.wa-stock', el).getBoundingClientRect(), b = lbcard.getBoundingClientRect();
      lbcard.style.transition = 'transform 900ms var(--vc-ease-io)';
      lbcard.style.transform = 'translate(' + (s.left - b.left) + 'px,' + (s.top - b.top) + 'px) scale(' + (s.width / b.width) + ',' + (s.height / b.height) + ')';
      veil.style.transition = 'opacity .9s var(--vc-ease-io)';
      panel.style.transition = 'opacity .6s var(--vc-ease-out)'; panel.style.opacity = '0';
      veil.style.opacity = '0';
      setTimeout(finish, 900);
    } else finish();
  }
  function show(i, dir) {
    if (busy || i === lbIdx) return;
    const p = photos[i];
    if (reduced() || !dir) {
      busy = true;
      lbcard.classList.add('swap'); info.classList.add('swap'); lbcard.style.opacity = '0';
      setTimeout(() => {
        lbIdx = i; fillCard(p); fillInfo(p); markLifted(i); scroller.scrollTop = 0; lbcard.style.opacity = ''; info.classList.remove('swap');
        setTimeout(() => { lbcard.classList.remove('swap'); busy = false; }, SWAP_MS);
      }, reduced() ? 0 : SWAP_MS);
      return;
    }
    busy = true;
    lbcard.classList.add('swap'); info.classList.add('swap');
    lbcard.style.opacity = '0'; lbcard.style.transform = 'translateX(' + (-40 * dir) + 'px) rotate(' + (-1.2 * dir) + 'deg)';
    setTimeout(() => {
      lbIdx = i; lbcard.classList.remove('swap'); fillCard(p); fillInfo(p); markLifted(i); if (mqMobile.matches || window.innerWidth <= 1100) scroller.scrollTop = 0;
      lbcard.style.transform = 'translateX(' + (40 * dir) + 'px) rotate(' + (1.2 * dir) + 'deg)';
      void lbcard.offsetWidth;
      lbcard.classList.add('swap'); lbcard.style.opacity = ''; lbcard.style.transform = '';
      info.classList.remove('swap');
      setTimeout(() => { lbcard.classList.remove('swap'); busy = false; }, SWAP_MS);
    }, SWAP_MS);
  }
  function step(d) {
    if (!visible.length) return;
    const k = visIdx.has(lbIdx) ? visIdx.get(lbIdx) : -1;
    show(visible[(k < 0 ? 0 : (k + d + visible.length) % visible.length)].i, d);
  }
  $('.js-prev').addEventListener('click', () => step(-1));
  $('.js-next').addEventListener('click', () => step(1));
  $('.js-close').addEventListener('click', () => closeLB());
  scroller.addEventListener('click', (e) => { if (e.target === e.currentTarget || e.target.classList.contains('dlg') || e.target === stage) closeLB(); });
  lb.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closeLB(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
    else if (e.key === 'Tab') {   // keep focus inside the dialog
      const f = $$('button,a[href]', lb).filter((x) => x.offsetParent !== null);
      if (!f.length) return;
      const a = f[0], z = f[f.length - 1];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); } else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    }
  });
  let sx = null, sy = 0;   // swipe
  stage.addEventListener('pointerdown', (e) => { if (e.pointerType === 'mouse') return; sx = e.clientX; sy = e.clientY; });
  stage.addEventListener('pointerup', (e) => {
    if (sx == null) return; const dx = e.clientX - sx, dy = e.clientY - sy; sx = null;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) step(dx < 0 ? 1 : -1);
  });
  stage.addEventListener('pointercancel', () => { sx = null; });

  /* ---------- what lives on the window and the document, and the observers: taken up on connect, let go on disconnect ---------- */
  let rz = 0, moreObs = null, stripObs = null;
  const onResize = () => {
    cancelAnimationFrame(rz);
    rz = requestAnimationFrame(() => {
      if (album.clientWidth !== lastW) apply(false);
      placeRules(); drawYou();
      if (lbOpen) { lbcard._gk = null; applyGeom(lbcard, lbGeom(photos[lbIdx])); }
      drawView();
    });
  };
  // the observer below only reports a change of state, so a jump past the end of the album (End, the scrollbar, a link) would never mount the next
  // chunk: checkMore() is cheap (it returns at once when everything is mounted), so a scroll asks it too, once a frame
  let sc = 0;
  const onScroll = () => { if (!sc) sc = requestAnimationFrame(() => { sc = 0; checkMore(); }); };
  const onPointerDown = (e) => selects.forEach((s) => { if (!s.contains(e.target) && s.classList.contains('open')) s._close(); });
  const onMobile = () => { closeSheet(false); strip.classList.remove('on'); watchFilters(); updateUI(); };
  function watchFilters() {   // desktop: the strip fades in once the filter bar has gone under the site header
    if (stripObs) stripObs.disconnect();
    stripObs = new IntersectionObserver((ents) => {
      const e = ents[0];
      strip.classList.toggle('on', !mqMobile.matches && !e.isIntersecting && e.boundingClientRect.bottom < headerH() + 1);
      requestAnimationFrame(placeRules);
    }, { rootMargin: '-' + headerH() + 'px 0px 0px 0px' });
    stripObs.observe(filters);
  }
  function attach() {
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('pointerdown', onPointerDown);
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', onMobile);
    moreObs = new IntersectionObserver((ents) => { if (ents[0].isIntersecting) checkMore(); }, { rootMargin: '0px 0px ' + BOTTOM_KEEP + 'px 0px' });
    moreObs.observe(more);
    io = new IntersectionObserver((ents) => ents.forEach((e) => { if (e.isIntersecting) { load(e.target); io.unobserve(e.target); } }), { rootMargin: '900px 0px' });
    cards.forEach((el) => { if (el && !$('img', el).getAttribute('src')) io.observe(el); });
    watchFilters();
  }
  function detach() {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll);
    cancelAnimationFrame(sc); sc = 0;
    document.removeEventListener('pointerdown', onPointerDown);
    if (mqMobile.removeEventListener) mqMobile.removeEventListener('change', onMobile);
    [moreObs, stripObs, io].forEach((o) => o && o.disconnect());
    moreObs = stripObs = io = null;
    clearTimeout(liveT); clearTimeout(kt);
    if (lbOpen) closeLB(true);
    if (sheetOpen) closeSheet(false);
  }

  /* ---------- boot ---------- */
  const byS = { atelier: 0, muse: 0, client: 0 }; photos.forEach((p) => { byS[p.source]++; });
  $('.js-total').textContent = photos.length;
  $('.js-split').innerHTML = ['atelier', 'muse', 'client'].map((k) => '<span><b>' + byS[k] + '</b>' + SRC[k].plural + '</span>').join('');
  buildSwatches();
  selects.push(makeSelect($('.js-sel-pc'), 'Piece', pcOpts, 'pc'));
  buildRuler();
  const saved = lsGet(); if (saved) st.you = saved;
  if (photos.length >= IX_AUTO) st.view = 'index';   // hundreds of portraits open as a contact sheet; the reader can switch back
  const wantCw = params.get('cw');   // the strip's "See all on Worn"
  if (wantCw && (CW[wantCw] || wantCw === CURATED)) st.cw = wantCw;
  let hashed = ''; try { hashed = decodeURIComponent((location.hash || '').slice(1)); } catch (e) { /* a malformed #fragment names nothing */ }
  const hit = hashed ? photos.filter((p) => p.id === hashed)[0] : null;   // a strip card's link (/pages/worn?cw=<handle>#<portrait id>)
  if (hit && !pool().includes(hit)) st.cw = 'all';
  album.classList.toggle('is-ix', st.view === 'index');
  attach();
  apply(false);
  root.classList.add('is-built');
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { placeRules(); drawYou(); drawView(); cards.forEach((el) => { if (el) el._fit = 0; }); fitNames(visible.slice(0, limit).filter((p) => p.ppl.length > 1).map((p) => cards[p.i])); });
  if (hit) {
    const k = visIdx.get(hit.i);
    if (k >= limit) { limit = Math.ceil((k + 1) / CHUNK) * CHUNK; apply(false); }
    load(cards[hit.i]); openLB(hit.i, cards[hit.i], true);
  }
  return { attach, detach };
}

class VcWornAlbum extends HTMLElement {
  connectedCallback() {
    if (this.impl) { this.impl.attach(); return; }   // moved, or the theme editor put it back: take up the listeners again (the DOM is built)
    if (this.classList.contains('is-built') || this.classList.contains('is-empty')) return;
    // ?state=empty forces the empty state (the operator's review aid; the brief), whatever the album holds
    if (new URLSearchParams(location.search).get('state') === 'empty') { this.classList.add('is-empty'); return; }
    try {
      this.impl = mount(this);
    } catch (err) {
      console.warn('[vc-worn-album]', err);
      this.classList.add('is-fallback');
    }
  }

  disconnectedCallback() { if (this.impl) this.impl.detach(); }
}
if (!customElements.get('vc-worn-album')) customElements.define('vc-worn-album', VcWornAlbum);
